import {
  AssessmentGroupListFragmentFragment,
  ComplianceProgramStep,
  OrganizationTalentIndicatorFragment,
} from "types/graphql-schema";
import { FormError } from "types/topicflow";

import { classNames } from "@helpers/css";

import ComplianceProgramFormStep from "../steps/compliance-program-form-step";
import EngagementSurveyStep from "../steps/engagement-survey-step";
import PeerAssessmentStep from "../steps/peer-assessment-step";
import ReviewPreview from "../steps/review-preview";
import SelfManagerAssessmentStep from "../steps/self-manager-assessment-step";
import UpwardAssessmentStep from "../steps/upward-assessment-step";
import { ProgramForm, ProgramFormStep } from "../types";

const ProgramFormQuestions = ({
  proposedComplianceProgram,
  canUpdate,
  talentIndicatorData,
  availableAssessmentGroups,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
  onAddOrUpdateStep,
  errors,
}: {
  canUpdate: boolean;
  proposedComplianceProgram: ProgramForm;
  talentIndicatorData?: OrganizationTalentIndicatorFragment[];
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (assessmentGroupId: number) => void;
  onAddOrUpdateStep: (step: ProgramFormStep) => void;
  errors: FormError[];
}) => {
  const activeSteps = proposedComplianceProgram.steps.map(
    ({ stepType }) => stepType,
  );
  const showQuestionSetError = errors.some(
    (error) => error.field === "questionSetIds",
  );
  // The review types the preview can show, as in ReviewPreview. Surveys have
  // none, so their configuration keeps the single column.
  const showsPreview =
    (activeSteps.includes(ComplianceProgramStep.SelfReview) &&
      activeSteps.includes(ComplianceProgramStep.DownwardReview)) ||
    activeSteps.includes(ComplianceProgramStep.PeerReview);
  // Side by side once the main area is wide enough, like the Workflow tab and
  // its Timeline. Until then each column keeps the single column's width.
  // Once wide, each column fills the height and scrolls on its own.
  const columnClassName = classNames(
    "mx-auto w-full min-w-0 max-w-(--breakpoint-md) @[1200px]/layout-main:min-h-0 @[1200px]/layout-main:flex-1",
    showsPreview &&
      "@[1200px]/layout-main:mx-0 @[1200px]/layout-main:max-w-none",
  );
  return (
    <div
      id="field-questionSetIds"
      className={classNames(
        "flex flex-col gap-14 scroll-mt-24 @[1200px]/layout-main:min-h-0 @[1200px]/layout-main:flex-1",
        showsPreview &&
          "@[1200px]/layout-main:flex-row @[1200px]/layout-main:gap-16",
      )}
    >
      <ComplianceProgramFormStep
        title="Question Configuration"
        // The preview's padding, so the two titles line up.
        className={classNames(columnClassName, "@[1200px]/layout-main:py-6")}
        scrollable
      >
        {activeSteps.includes(ComplianceProgramStep.SelfReview) &&
          activeSteps.includes(ComplianceProgramStep.DownwardReview) && (
            <SelfManagerAssessmentStep
              proposedComplianceProgram={proposedComplianceProgram}
              canUpdate={canUpdate}
              talentIndicatorData={talentIndicatorData}
              availableAssessmentGroups={availableAssessmentGroups}
              onAssessmentGroupCreated={onAssessmentGroupCreated}
              onAssessmentGroupArchived={onAssessmentGroupArchived}
              onAddOrUpdateStep={onAddOrUpdateStep}
              showQuestionSetError={showQuestionSetError}
            />
          )}

        {activeSteps.includes(ComplianceProgramStep.UpwardReview) && (
          <UpwardAssessmentStep
            proposedComplianceProgram={proposedComplianceProgram}
            canUpdate={canUpdate}
            talentIndicatorData={talentIndicatorData}
            onAddOrUpdateStep={onAddOrUpdateStep}
            availableAssessmentGroups={availableAssessmentGroups}
            onAssessmentGroupCreated={onAssessmentGroupCreated}
            onAssessmentGroupArchived={onAssessmentGroupArchived}
            showQuestionSetError={showQuestionSetError}
          />
        )}

        {activeSteps.includes(ComplianceProgramStep.PeerReview) && (
          <PeerAssessmentStep
            proposedComplianceProgram={proposedComplianceProgram}
            canUpdate={canUpdate}
            onAddOrUpdateStep={onAddOrUpdateStep}
            availableAssessmentGroups={availableAssessmentGroups}
            onAssessmentGroupCreated={onAssessmentGroupCreated}
            onAssessmentGroupArchived={onAssessmentGroupArchived}
            showQuestionSetError={showQuestionSetError}
          />
        )}

        {activeSteps.includes(ComplianceProgramStep.EngagementSurvey) && (
          <EngagementSurveyStep
            proposedComplianceProgram={proposedComplianceProgram}
            canUpdate={canUpdate}
            onAddOrUpdateStep={onAddOrUpdateStep}
            availableAssessmentGroups={availableAssessmentGroups}
            onAssessmentGroupCreated={onAssessmentGroupCreated}
            onAssessmentGroupArchived={onAssessmentGroupArchived}
            showQuestionSetError={showQuestionSetError}
          />
        )}
      </ComplianceProgramFormStep>

      {showsPreview && (
        <ReviewPreview
          // A shade darker than the page, to set the preview apart.
          className={classNames(columnClassName, "rounded-lg bg-gray-100 p-6")}
          proposedComplianceProgram={proposedComplianceProgram}
          talentIndicatorData={talentIndicatorData}
        />
      )}
    </div>
  );
};

export default ProgramFormQuestions;
